import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * Unlike the product form, the cart allows stepping below the minimum quantity:
 * the minus button stays active at the lowest valid quantity and removes the line
 * instead of doing nothing (see updateQuantity / setQuantity below).
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are managed client-side unless the server disabled them
   * (e.g. bundle components, which cannot be changed at all).
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Minus stays active down to 0 — stepping below the minimum removes the line.
    minusButton.disabled = this.serverDisabledMinus || value <= 0;
    plusButton.disabled = this.serverDisabledPlus || (effectiveMax !== null && value >= effectiveMax);
  }

  /**
   * Updates quantity by a given step.
   * Decreasing below the minimum quantity sets 0, which removes the cart line.
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    if (stepMultiplier >= 0) return super.updateQuantity(stepMultiplier);

    const { min, step, value } = this.getCurrentValues();

    if (value - step >= min) return super.updateQuantity(stepMultiplier);

    this.#removeLine();
  }

  /**
   * Handles the quantity set event (on blur).
   * Typing 0 (or less) removes the cart line instead of snapping back to the minimum.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    if ((parseInt(event.target.value, 10) || 0) <= 0) {
      event.preventDefault();
      this.#removeLine();
      return;
    }

    super.setQuantity(event);
  }

  /**
   * Sets the quantity to 0 and notifies the cart, which removes the line.
   */
  #removeLine() {
    const { quantityInput } = this.refs;

    quantityInput.value = '0';
    this.onQuantityChange();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
